import { useId, useState } from 'react';
import { OVERVIEW_MODULE_LABELS, type OverviewNextAction } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { CtaLink, useLookOnlyReason } from './parts.js';

const KICKER: Record<OverviewNextAction['kind'], string> = {
  ACT: 'Next best action',
  CHOOSE: 'Next best action',
  WAIT: 'In progress',
  UP_TO_DATE: 'Up to date',
  UNAVAILABLE: 'Next best action',
};

/**
 * THE ONE THING TO DO NEXT, as the server worked it out (`deriveOverview`).
 *
 * The slate is the page's strongest object on purpose: everything else on the
 * Overview answers "where are we", this answers "what now". Its four shapes:
 *
 * - ACT: one primary action.
 * - CHOOSE: two equally valid ways on, drawn as equals — neither workflow is a
 *   prerequisite of the other, and a red button beside a white one would say it was.
 * - WAIT: work is running; the indeterminate sweep says so and claims no percentage.
 * - UP_TO_DATE: nothing waits on a person, said quietly, never as "complete".
 *
 * "WHY THIS MATTERS" is the server's sentence (`why`), closed until asked for:
 * the description says what to do, this says what depends on it. The panel
 * draws the control only when the server sent one.
 */
export function NextBestAction({
  next,
  address,
  readOnly,
  onRetry,
}: {
  next: OverviewNextAction;
  address: string;
  readOnly: boolean;
  /** Offered when the facts could not be read: asking again is the whole remedy. */
  onRetry?: () => void;
}) {
  const reason = useLookOnlyReason(readOnly);
  const [whyOpen, setWhyOpen] = useState(false);
  const whyId = useId();
  // An API older than this page sends no `why` at all: read that as none, so a
  // mixed deploy draws no empty "Why this matters".
  const why = typeof next.why === 'string' && next.why !== '' ? next.why : null;
  const refusals = [...new Set(next.ctas.map(reason).filter((r): r is string => r !== null))];
  const onInk = next.kind === 'ACT' || next.kind === 'CHOOSE' || next.kind === 'WAIT';

  const className = (index: number, executable: boolean): string => {
    if (next.kind === 'CHOOSE') return 'btn cc-choice';
    if (next.kind === 'WAIT' || !executable) return onInk ? 'btn cc-on-ink-ghost' : 'btn btn-ghost';
    if (!onInk) return 'btn btn-ghost';

    return index === 0 ? 'btn btn-primary' : 'btn cc-on-ink-ghost';
  };

  return (
    <section
      className="cc-slate"
      data-kind={next.kind}
      data-testid="overview-next-action"
      aria-labelledby="overview-next-action-title"
    >
      <div style={{ minWidth: 0 }}>
        <p className="cc-slate-kicker">
          <span>{KICKER[next.kind]}</span>
          {next.module === null ? null : (
            <span className="cc-slate-module" data-testid="overview-next-action-module">
              {OVERVIEW_MODULE_LABELS[next.module]}
            </span>
          )}
        </p>
        <h2 className="cc-slate-title" id="overview-next-action-title" data-testid="overview-next-action-title">
          {next.title}
        </h2>
        <p className="cc-slate-text" data-testid="overview-next-action-text">
          {next.description}
        </p>
        {why === null ? null : (
          <p id={whyId} className="cc-slate-why" hidden={!whyOpen} data-testid="overview-next-action-why">
            {why}
          </p>
        )}
        {next.kind === 'WAIT' ? (
          <div className="sa-sweep-track" style={{ marginTop: 16, maxWidth: 420 }} role="status" aria-label="Working">
            <div className="sa-sweep-bar" />
          </div>
        ) : null}
        {refusals.map((r) => (
          <p key={r} className="cc-slate-note" data-testid="overview-next-action-refusal">
            {r}
          </p>
        ))}
      </div>

      {next.kind === 'UNAVAILABLE' && onRetry !== undefined ? (
        <div className="cc-slate-ctas">
          <button type="button" className="btn btn-ghost" data-testid="overview-next-action-retry" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}

      {next.ctas.length > 0 || why !== null ? (
        <div className="cc-slate-ctas">
          {next.ctas.map((cta, i) => (
            <CtaLink
              key={`${cta.label}-${String(i)}`}
              address={address}
              cta={cta}
              className={className(i, cta.executable)}
              testId={`overview-next-action-cta-${String(i)}`}
            />
          ))}
          {why === null ? null : (
            <button
              type="button"
              className="cc-why"
              aria-expanded={whyOpen}
              aria-controls={whyId}
              data-testid="overview-next-action-why-toggle"
              onClick={() => {
                setWhyOpen((open) => !open);
              }}
            >
              Why this matters
              <Icon name="info" size={15} />
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
